import type {
  AccommodationAssignment, AccommodationFilter, AccommodationImportIssue, AccommodationStatus,
  AccommodationImportItem, AccommodationImportResponse, AccommodationParticipantsResponse,
  AccommodationResponse, CertStatus, CheckInResponse, EventSettings,
  GetSlotResponse, LoginResponse, OtpRequestResponse, ParticipantProfile,
  QrGetSlotResponse, SaveProfileResponse, Slot,
  ExtendSlotResponse,
  AttendanceSummaryResponse,
  AttendanceDetailResponse,
} from '../lib/types'
import { ROLE_PARTICIPANT } from '../lib/types'
import type {
  ManualCheckInResponse, QrCodeResponse, SoundCodeResponse, VenueAttendanceResponse,
} from '../lib/types'
import { DEMO_EVENT } from '../lib/event-config'
import { EVENT, mytDateKey } from '../config/event'
import { isClockShifted, nowMs } from '../lib/clock'

/**
 * Fixtures used when the API is unreachable, so every screen still renders.
 *
 * These mirror the real seeded programme for `id_event` 6 — the CPD 2026 tentative
 * (LAMPIRAN C) with its dates shifted from 9–11 September to 24–26 August 2026 for
 * testing. What you see here is one track's view: **Pegawai Perubatan/Penolong
 * Pegawai Perubatan**, the same track the demo account is registered on, so demo
 * mode and live mode show the same 21 slots.
 *
 * The live event also carries four other parallel tracks (Pergigian, Jururawat,
 * Farmasi, Pembantu Perawatan Kesihatan/Pemandu Ambulans). They are deliberately not
 * duplicated here — a fixture is one participant's programme, not the whole event.
 */

/*
 * Fixed demo clock: 11:11 MYT on day 1, so live/upcoming/past states are all reachable
 * from `npm run demo` without anyone having to know the event's dates.
 *
 * It yields to the development clock when one is set. Without that, moving the clock to
 * a live session showed a mismatch rather than a live session: the client computed the
 * window from the shifted time while the fixture kept insisting it was 11:11 on day
 * one, so `show_btn` and `onGoing` described a different moment from everything around
 * them.
 */
const MYT_CLOCK = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Asia/Kuala_Lumpur',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
})

function mytMinutesNow(): number {
  if (!isClockShifted()) return 11 * 60 + 11
  const [h, m] = MYT_CLOCK.format(nowMs()).split(':').map(Number)
  return h * 60 + m
}

function mytToday(): string {
  return isClockShifted() ? mytDateKey(nowMs()) : EVENT.startDate
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

function minutesOf(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number)
  return h * 60 + m
}

/** The API serialises wall-clock digits with a `Z` it has not earned; fixtures do the same. */
function stampFor(dateIso: string, hhmm: string): string {
  return `${dateIso}T${hhmm}:00.000Z`
}

function dateLabels(dateIso: string): { short: string; full: string } {
  const [y, m, d] = dateIso.split('-').map(Number)
  const dd = String(d).padStart(2, '0')
  return { short: `${dd} ${MONTHS[m - 1]}`, full: `${dd} ${MONTHS[m - 1]} ${y}` }
}

interface Entry {
  d: string
  from: string
  to: string
  name: string
  speaker: string | null
  venue: string
  qr: boolean
  initial: boolean
  off: number
}

/* Generated from the CPD 2026 tentative, Pegawai Perubatan/PPP track. */
const PROGRAMME: Entry[] = [
  { d: '2026-08-24', from: '15:00', to: '18:00', name: "Pendaftaran/Minum Petang", speaker: null, venue: "Foyer ILD, UiTM Bandar Enstek", qr: true, initial: true, off: 0 },
  { d: '2026-08-24', from: '19:00', to: '20:00', name: "Makan Malam", speaker: null, venue: "Cafe Anjung Sendayan, ILD UiTM Bandar Enstek", qr: false, initial: false, off: 0 },
  { d: '2026-08-24', from: '20:00', to: '22:00', name: "Sesi Bersama: Integriti dan Profesionalisme Staf Kesihatan - Cabaran dan Tanggungjawab", speaker: null, venue: "Bilik Sarana 3, 4 & 5", qr: true, initial: false, off: 90 },
  { d: '2026-08-25', from: '07:00', to: '08:00', name: "Sarapan Pagi", speaker: null, venue: "Cafe Anjung Sendayan, ILD UiTM Bandar Enstek", qr: false, initial: false, off: 0 },
  { d: '2026-08-25', from: '08:00', to: '09:00', name: "HIV Awareness: Know, Prevent, Protect", speaker: "Prof Madya Dr Ilham Ameera, FMS, Ketua Unit Khidmat Masyarakat, HASA", venue: "Bilik Sarana 1", qr: true, initial: false, off: 30 },
  { d: '2026-08-25', from: '09:00', to: '11:00', name: "Skin Made Simple: Mastering Common Skin Diseases", speaker: "Dr Suriani Sulaiman, FMS KK Presint 9", venue: "Bilik Sarana 1", qr: true, initial: false, off: 90 },
  { d: '2026-08-25', from: '11:00', to: '13:00', name: "Medicolegal: Understanding Law and Ethics in Medical Practice", speaker: "Dr Ahmad Khairil Faizi, Mantan Pengarah Bahagian Etika & Perundangan, MMC", venue: "Bilik Sarana 1", qr: true, initial: false, off: 90 },
  { d: '2026-08-25', from: '13:00', to: '14:00', name: "Makan Tengahari", speaker: null, venue: "Cafe Anjung Sendayan, ILD UiTM Bandar Enstek", qr: false, initial: false, off: 0 },
  { d: '2026-08-25', from: '14:00', to: '15:00', name: "Breaking the Silence: Self-Harm vs Suicide", speaker: "Dr Norfaridah Masiran, FMS, KK Kajang", venue: "Bilik Sarana 1", qr: true, initial: false, off: 30 },
  { d: '2026-08-25', from: '15:00', to: '17:00', name: "Skim Perkhidmatan Pelajar, Tabung Kecemasan, e-Medical Report (MO Sahaja)", speaker: "Dr Shahrul Azam Sahar, Pengarah Pusat Kesihatan UiTM", venue: "Bilik Sarana 1", qr: true, initial: false, off: 90 },
  { d: '2026-08-25', from: '16:30', to: '17:00', name: "Minum Petang", speaker: null, venue: "Cafe Anjung Sendayan, ILD UiTM Bandar Enstek", qr: false, initial: false, off: 0 },
  { d: '2026-08-25', from: '17:00', to: '19:00', name: "Minum/Riadah Petang", speaker: null, venue: "ILD UiTM Bandar Enstek", qr: false, initial: false, off: 0 },
  { d: '2026-08-25', from: '20:00', to: '23:00', name: "Majlis Makan Malam", speaker: null, venue: "Cafe Anjung Sendayan, ILD UiTM Bandar Enstek", qr: false, initial: false, off: 0 },
  { d: '2026-08-26', from: '06:30', to: '07:30', name: "Riadah Pagi", speaker: null, venue: "ILD UiTM Bandar Enstek", qr: false, initial: false, off: 0 },
  { d: '2026-08-26', from: '07:30', to: '08:30', name: "Sarapan Pagi", speaker: null, venue: "Cafe Anjung Sendayan, ILD UiTM Bandar Enstek", qr: false, initial: false, off: 0 },
  { d: '2026-08-26', from: '08:30', to: '09:00', name: "Slot 1: AstraZeneca", speaker: null, venue: "Bilik Sarana 3, 4 & 5", qr: true, initial: false, off: 15 },
  { d: '2026-08-26', from: '09:00', to: '09:30', name: "Slot 2: CLIS PK", speaker: null, venue: "Bilik Sarana 3, 4 & 5", qr: true, initial: false, off: 15 },
  { d: '2026-08-26', from: '09:30', to: '10:00', name: "Slot 3: Hyphens Pharma", speaker: null, venue: "Bilik Sarana 3, 4 & 5", qr: true, initial: false, off: 15 },
  { d: '2026-08-26', from: '10:00', to: '10:30', name: "Slot 4: Zuellig Pharma", speaker: null, venue: "Bilik Sarana 3, 4 & 5", qr: true, initial: false, off: 15 },
  { d: '2026-08-26', from: '10:30', to: '12:00', name: "Majlis Penutup", speaker: null, venue: "Bilik Sarana 3, 4 & 5", qr: true, initial: false, off: 45 },
  { d: '2026-08-26', from: '12:00', to: '14:00', name: "Makan Tengahari dan Bersurai", speaker: null, venue: "Cafe Anjung Sendayan, ILD UiTM Bandar Enstek", qr: false, initial: false, off: 0 },
]

function clock12(hhmm: string): string {
  const [h, m] = hhmm.split(':').map(Number)
  const ampm = h >= 12 ? 'PM' : 'AM'
  return `${h % 12 || 12}:${String(m).padStart(2, '0')}${ampm}`
}

const SLOTS: Slot[] = PROGRAMME.map((e, i) => {
  const labels = dateLabels(e.d)
  const id = 601 + i
  return {
    id,
    slot_name: e.name,
    slot_begin: stampFor(e.d, e.from),
    slot_end: stampFor(e.d, e.to),
    offset_checkin_minute: e.off,
    qr_required: e.qr,
    slot_date_short: labels.short,
    slot_date_full: labels.full,
    slot_time: `${clock12(e.from)} - ${clock12(e.to)}`,
    initial_register: e.initial ? 1 : 0,
    speaker: e.speaker,
    venue_name: e.venue,
    extended_time: 0,
    qr_uuid: `f1e2d3c4-${String(id).padStart(4, '0')}-4b6a-9c8d-7e5f0a1b2c3d`,
  }
})

/**
 * The demo participant has attended most, but deliberately not all, attendance-required
 * sessions. Non-QR items (meals, breaks, riadah) never count. This makes the demo
 * exercise the real rule: only `qr_required` slots feed attendance and eligibility.
 */
/*
 * Checked against DEMO_EVENT, not against EVENT.
 *
 * EVENT is a live view of whatever GET /event/current resolved, and reading it here
 * would now throw outright - at module-evaluation time nothing has resolved yet, which
 * is exactly the ordering bug eventConfig() is written to shout about.
 *
 * DEMO_EVENT is the event these fixtures depict, and demo mode never calls the server,
 * so it is the only thing they can meaningfully be checked against.
 */
const MOCK_DAY_KEYS = [...new Set(SLOTS.map((slot) => slot.slot_begin.slice(0, 10)))]
const EXPECTED_DAYS = `${DEMO_EVENT.startDate},2026-08-25,${DEMO_EVENT.endDate}`
if (MOCK_DAY_KEYS.length !== 3 || MOCK_DAY_KEYS.join(',') !== EXPECTED_DAYS) {
  throw new Error(
    `CPD demo fixture must contain ${EXPECTED_DAYS}; got ${MOCK_DAY_KEYS.join(', ')}`,
  )
}

const REQUIRED_SLOTS = SLOTS.filter((slot) => Boolean(slot.qr_required))
const scanned = new Set<number>()

// 9 of the 12 attendance-required sessions are attended: 75%, just over the 70%
// threshold the seeded `setting_apps` row uses, so the certificate is reachable while
// three unscanned sessions still demonstrate partial attendance.
REQUIRED_SLOTS.slice(0, 9).forEach((slot) => scanned.add(slot.id))

if (scanned.size !== 9 || REQUIRED_SLOTS.length !== 12) {
  throw new Error(
    `CPD demo attendance fixture expected 9/12 required sessions; got ${scanned.size}/${REQUIRED_SLOTS.length}`,
  )
}

function toMinutes(stampValue: string): number {
  return minutesOf(stampValue.slice(11, 16))
}

function decorate(): Slot[] {
  const now = mytMinutesNow()
  const today = mytToday()
  return SLOTS.map((slot) => {
    const isToday = slot.slot_begin.slice(0, 10) === today
    const start = toMinutes(slot.slot_begin)
    const end = toMinutes(slot.slot_end)
    const live = isToday && now >= start && now <= end
    const unlocksAt = slot.initial_register ? start : end - slot.offset_checkin_minute
    const unlocked = live && now >= unlocksAt
    return {
      ...slot,
      show_btn: Boolean(slot.qr_required && unlocked && !scanned.has(slot.id)),
      checkin_status: scanned.has(slot.id),
    }
  })
}

function ongoing(slots: Slot[]): Slot[] {
  const now = mytMinutesNow()
  const today = mytToday()
  return slots.filter((slot) => {
    if (slot.slot_begin.slice(0, 10) !== today) return false
    return now >= toMinutes(slot.slot_begin) && now <= toMinutes(slot.slot_end)
  })
}

const DEMO = {
  email: 'demo.participant@uitm.edu.my',
  name: 'Dr. Amirah Binti Kamarudin',
  track: 'Pegawai Perubatan/Penolong Pegawai Perubatan',
}

const MOCK_TRACKS = [
  { id: 1, track_name: DEMO.track },
  { id: 2, track_name: 'Jururawat' },
  { id: 3, track_name: 'Farmasi' },
  { id: 4, track_name: 'Pergigian' },
]
let mockProfileName = DEMO.name
let mockTrackId = 1
let mockProfileVerified = false

/*
 * Deliberately mixed: enough assigned and unassigned rows that the organiser's filter
 * chips and the "x of y assigned" progress line have something to show in demo mode.
 * Sorted by name, the way the server orders the real roster.
 */
const MOCK_ACCOMMODATION: AccommodationAssignment[] = [
  { email: 'aisyah.karim@uitm.edu.my', name: 'Dr. Aisyah Karim', room_number: '402', status: 'assigned', key_collected_at: '2026-09-09T06:12:00.000Z' },
  { email: 'farah.nordin@uitm.edu.my', name: 'Dr. Farah Nordin', room_number: null, status: 'assigned', key_collected_at: null },
  { email: 'izzat.rahman@uitm.edu.my', name: 'Encik Izzat Rahman', room_number: '214', status: 'assigned', key_collected_at: null },
  // Two delegates bringing family, so demo mode shows the third state rather than
  // implying every empty room is outstanding work.
  { email: 'lina.tan@uitm.edu.my', name: 'Puan Lina Tan', room_number: null, status: 'own_arrangement', key_collected_at: null },
  { email: DEMO.email, name: DEMO.name, room_number: '318', status: 'assigned', key_collected_at: null },
  { email: 'ravi.kumar@uitm.edu.my', name: 'Encik Ravi Kumar', room_number: null, status: 'assigned', key_collected_at: null },
  { email: 'suhaila.omar@uitm.edu.my', name: 'Dr. Suhaila Omar', room_number: '215', status: 'assigned', key_collected_at: null },
  { email: 'zaki.hamid@uitm.edu.my', name: 'Encik Zaki Hamid', room_number: null, status: 'own_arrangement', key_collected_at: null },
]

function session(extra: Partial<LoginResponse> = {}): LoginResponse {
  const currentTrack = MOCK_TRACKS.find((track) => track.id === mockTrackId) ?? MOCK_TRACKS[0]
  return {
    auth: true,
    email: DEMO.email,
    name: mockProfileName,
    at: 'mock.jwt.token',
    img: null,
    track_name: currentTrack.track_name,
    track_id: currentTrack.id,
    verified_name: mockProfileVerified,
    id_role: ROLE_PARTICIPANT,
    ...extra,
  }
}

export function loginResponse(): LoginResponse { return session() }

export function passwordLogin(email: string, password: string): LoginResponse {
  if (!password) return { auth: false }
  return session({ email })
}

export function requestOtp(email: string): OtpRequestResponse {
  if (!email.includes('@')) return { user_status: false }
  return { user_status: true, role: ROLE_PARTICIPANT, message: 'OTP sent successfully' }
}

export function verifyOtp(email: string, otp: string): LoginResponse {
  if (otp !== '123456') return { auth: false, otp: false }
  return session({ email, otp: true })
}

export function getSlots(): GetSlotResponse {
  const slots = decorate()
  return { onGoing: ongoing(slots), slots, total_checkin: scanned.size }
}

export function venueSlots(): QrGetSlotResponse {
  const slots = SLOTS.filter((s) => Boolean(s.qr_required))
  return { onGoing: ongoing(slots), slots }
}

/*
 * Mutates the fixture rather than echoing the number back. The venue screen renders the
 * session's end time from `extended_time`, so an echo would move the label while the
 * programme behind it disagreed - demo mode would be demonstrating something the real
 * app does not do.
 */
/*
 * Attendance reporting on fixtures.
 *
 * Derived from the same `scanned` set the rest of demo mode uses, so the report agrees
 * with the attendance screens rather than inventing its own numbers. One participant,
 * so `expected` is 1 on every slot of this track - which is honest for a fixture and is
 * exactly why demo mode is not where you check whether the denominator is right.
 */
export function attendanceSummary(): AttendanceSummaryResponse {
  return {
    ok: true,
    slots: SLOTS.map((slot) => ({
      id: slot.id,
      slot_name: slot.slot_name,
      venue_name: slot.venue_name,
      slot_date: slot.slot_begin.slice(0, 10),
      slot_time: slot.slot_time,
      qr_required: Boolean(slot.qr_required),
      attended: scanned.has(slot.id) ? 1 : 0,
      expected: Boolean(slot.qr_required) ? 1 : 0,
    })),
  }
}

export function attendanceDetail(id_slot?: number): AttendanceDetailResponse {
  const rows = SLOTS
    .filter((slot) => scanned.has(slot.id) && (id_slot === undefined || slot.id === id_slot))
    .map((slot) => ({
      id_slot: slot.id,
      slot_name: slot.slot_name,
      venue_name: slot.venue_name,
      slot_date: slot.slot_begin.slice(0, 10),
      slot_time: slot.slot_time,
      name: mockProfileName,
      email: DEMO.email,
      track_name: DEMO.track,
      checked_in_at: `${slot.slot_begin.slice(0, 10)} ${slot.slot_begin.slice(11, 19)}`,
    }))
  return { ok: true, rows }
}

export function extendSlot(id_slot: number, extended_time: number): ExtendSlotResponse {
  const slot = SLOTS.find((s) => s.id === id_slot)
  if (!slot) return { ok: false, id_slot, extended_time: 0 }
  slot.extended_time = extended_time
  return { ok: true, id_slot, extended_time }
}

export function checkIn(qr_uuid: string, slot_id_toscan: number): CheckInResponse {
  const slot = SLOTS.find((s) => s.id === slot_id_toscan)
  if (!slot) return { msg: 'Invalid QR code.' }
  const rotating = qr_uuid.startsWith('cpd1:') ? qr_uuid.slice(5) : null
  if (rotating !== null) {
    if (rotating !== demoSoundCode(slot_id_toscan)) return { msg: 'That code has expired or is for a different session. Scan the screen again.' }
  } else if (slot.qr_uuid !== qr_uuid) {
    return { msg: 'Invalid QR code.' }
  } else if (!staticSlots.has(slot_id_toscan)) {
    return { msg: 'That code is out of date. Scan the code on the screen.' }
  }
  scanned.add(slot_id_toscan)
  return { msg: 'Checkin success.' }
}

/* The demo code is derived from the slot id, so the display and the phone agree. */
function demoSoundCode(slot_id: number): string {
  return `DEMO${String(slot_id).padStart(4, '0')}`
}

export function checkInBySound(sound_code: string, slot_id_toscan: number): CheckInResponse {
  if (!mockSettings.sound_checkin) return { msg: 'Sound check-in is not enabled for this event.' }
  if (sound_code !== demoSoundCode(slot_id_toscan)) return { msg: 'Invalid QR code.' }
  scanned.add(slot_id_toscan)
  return { msg: 'Checkin success.' }
}

const staticSlots = new Set<number>()

export function qrCode(id_slot: number): QrCodeResponse {
  return {
    ok: true, id_slot, static_qr: staticSlots.has(id_slot),
    payload: `cpd1:${demoSoundCode(id_slot)}`, expires_in_ms: 30_000, window_ms: 30_000,
  }
}

export function setStaticQr(id_slot: number, static_qr: boolean) {
  if (static_qr) staticSlots.add(id_slot)
  else staticSlots.delete(id_slot)
  return { ok: true, id_slot, static_qr }
}

export function venueAttendance(id_slot: number): VenueAttendanceResponse {
  return { ok: true, id_slot, attended: scanned.has(id_slot) ? 1 : 0, expected: 1 }
}

export function manualCheckIn(id_slot: number, participant_email: string): ManualCheckInResponse {
  const duplicate = scanned.has(id_slot)
  scanned.add(id_slot)
  return { ok: true, id_slot, participant_email, duplicate }
}

export function soundCode(id_slot: number): SoundCodeResponse {
  return { ok: true, id_slot, code: demoSoundCode(id_slot), expires_in_ms: 30_000, window_ms: 30_000 }
}

const mockSettings: EventSettings = {
  show_cert: true,
  /* On in the demo, so the "Listen" path can be exercised without a database. */
  sound_checkin: true,
  names_locked: false,
  attendance_link: false,
  attendance_url: null,
  attendance_on: false,
  // Left unset so demo mode shows the "coming soon" state, which is what an organiser
  // sees before they have anything to link to.
  gallery_url: null,
  resources_url: null,
  /*
   * Null, not EVENT.certificateAttendanceThreshold. This object is built when the module
   * is imported, and api.ts imports it unconditionally — reading EVENT here ran before
   * boot resolved the event and took the whole app down, live mode included. The demo's
   * threshold is resolved lazily below instead.
   */
  certificate_threshold: null,
}

export function getSettings(): EventSettings {
  return {
    ...mockSettings,
    certificate_threshold: mockSettings.certificate_threshold ?? EVENT.certificateAttendanceThreshold,
  }
}

export function saveSettings(
  show_cert: boolean,
  patch?: {
    gallery_url?: string; resources_url?: string; certificate_threshold?: number; sound_checkin?: boolean
    names_locked?: boolean; attendance_url?: string; attendance_on?: boolean
  },
): void {
  mockSettings.show_cert = show_cert
  if (patch?.sound_checkin !== undefined) mockSettings.sound_checkin = patch.sound_checkin
  if (patch?.names_locked !== undefined) mockSettings.names_locked = patch.names_locked
  if (patch?.gallery_url !== undefined) mockSettings.gallery_url = patch.gallery_url.trim() || null
  if (patch?.resources_url !== undefined) mockSettings.resources_url = patch.resources_url.trim() || null
  if (patch?.certificate_threshold !== undefined) {
    mockSettings.certificate_threshold = patch.certificate_threshold
  }
}

export function certStatus(): CertStatus {
  const required = SLOTS.filter((s) => Boolean(s.qr_required)).length
  /* The saved threshold, so changing it on the settings screen moves the demo too. */
  const threshold = mockSettings.certificate_threshold ?? EVENT.certificateAttendanceThreshold
  const eligible = scanned.size / required >= threshold / 100
  return {
    show_cert: mockSettings.show_cert,
    cert_eligable: eligible,
    feedback_done: 0,
    cert_url: eligible ? 'https://medical.uitm.edu.my/cdn/cpd/cert/demo.pdf' : null,
  }
}

export function getParticipantProfile(): ParticipantProfile {
  const currentTrack = MOCK_TRACKS.find((track) => track.id === mockTrackId)
  return {
    full_name: mockProfileName,
    verified_name: mockProfileVerified,
    name_locked: mockSettings.names_locked,
    track_id: currentTrack?.id ?? null,
    track_name: currentTrack?.track_name ?? '',
    tracks: MOCK_TRACKS,
  }
}

export function saveParticipantProfile(fullName: string, trackId: number): SaveProfileResponse {
  const selectedTrack = MOCK_TRACKS.find((track) => track.id === trackId) ?? MOCK_TRACKS[0]
  mockProfileName = fullName
  mockTrackId = selectedTrack.id
  mockProfileVerified = true
  return {
    is_success: true,
    full_name: mockProfileName,
    verified_name: mockProfileVerified,
    track_id: selectedTrack.id,
    track_name: selectedTrack.track_name,
  }
}

export function getAccommodation(): AccommodationResponse {
  const row = MOCK_ACCOMMODATION.find((candidate) => candidate.email === DEMO.email)
  return { room_number: row?.room_number ?? null, status: row?.status ?? 'assigned', key_collected_at: row?.key_collected_at ?? null }
}

export function setAccommodationKey(email: string, collected: boolean): { key_collected_at: string | null } {
  const row = MOCK_ACCOMMODATION.find((candidate) => candidate.email === email)
  if (!row || !row.room_number) return { key_collected_at: null }
  row.key_collected_at = collected ? new Date().toISOString() : null
  return { key_collected_at: row.key_collected_at }
}

export function listAccommodationParticipants(
  query: string,
  filter: AccommodationFilter = 'all',
  limit = 50,
  offset = 0,
): AccommodationParticipantsResponse {
  const needle = query.trim().toLowerCase()
  const matching = MOCK_ACCOMMODATION.filter((row) => {
    if (needle && !`${row.name} ${row.email}`.toLowerCase().includes(needle)) return false
    if (filter === 'assigned') return row.room_number !== null
    if (filter === 'own') return row.status === 'own_arrangement'
    // "Needs a room" is an empty room AND still expecting one.
    if (filter === 'unassigned') return row.room_number === null && row.status === 'assigned'
    if (filter === 'nokey') return row.room_number !== null && row.key_collected_at === null
    return true
  })
  const assigned = MOCK_ACCOMMODATION.filter((row) => row.room_number !== null).length
  const own = MOCK_ACCOMMODATION.filter((row) => row.status === 'own_arrangement').length
  return {
    participants: matching.slice(offset, offset + limit).map((row) => ({ ...row })),
    total: matching.length,
    limit,
    offset,
    counts: {
      total: MOCK_ACCOMMODATION.length,
      assigned,
      own_arrangement: own,
      unassigned: MOCK_ACCOMMODATION.length - assigned - own,
      keys_collected: MOCK_ACCOMMODATION.filter((row) => row.room_number !== null && row.key_collected_at !== null).length,
    },
  }
}

export function assignAccommodationRoom(
  email: string,
  roomNumber: string,
  status: AccommodationStatus = 'assigned',
): AccommodationResponse {
  const row = MOCK_ACCOMMODATION.find((candidate) => candidate.email === email)
  if (!row) return { room_number: null, status: 'assigned', key_collected_at: null }
  row.status = status
  row.room_number = status === 'own_arrangement' ? null : roomNumber.trim() || null
  if (!row.room_number) row.key_collected_at = null
  return { room_number: row.room_number, status: row.status, key_collected_at: row.key_collected_at }
}

/**
 * Mirrors the server's partial import: rows for people we know about are written, the
 * rest come back as issues. `ok` means nothing was rejected, so a partial import is
 * `ok: false` with a non-zero `imported`/`updated` — the same contract as the API.
 */
export function importAccommodationRooms(
  assignments: AccommodationImportItem[],
): AccommodationImportResponse {
  const issues: AccommodationImportIssue[] = []
  let imported = 0
  let updated = 0

  assignments.forEach((item, index) => {
    const row = MOCK_ACCOMMODATION.find(
      (candidate) => candidate.email.toLowerCase() === item.participant_email.toLowerCase(),
    )
    if (!row) {
      issues.push({
        line: index + 2,
        participant_email: item.participant_email,
        reason: 'Registered participant not found.',
      })
      return
    }
    if (row.room_number === null) imported += 1
    else updated += 1
    // Writing a room means they need one, so it overrides an earlier own_arrangement.
    row.room_number = item.room_number.trim() || null
    row.status = 'assigned'
  })

  return { ok: issues.length === 0, imported, updated, skipped: issues.length, issues }
}

export const MOCK_SLOTS = SLOTS

/* -------------------------------------------------------------- idea board */

import type { Idea, IdeaCategory, IdeaModule, IdeaStatus } from '../lib/types'

const mockIdeas: Idea[] = [
  { id: 1, name: 'DR AISYAH BINTI RAHMAN', mine: false, category: 'feature', module: 'consultation', body: 'Show a patient\'s last three visits on one screen without opening each one.', status: 'approved', pinned: true, created_at: '2026-09-02T03:10:00Z', votes: 14, voted: false },
  { id: 2, name: 'MOHD FAIZ BIN OSMAN', mine: false, category: 'improvement', module: 'pharmacy', body: 'Prescription screen: remember the last dose I used for a drug.', status: 'planned', pinned: false, created_at: '2026-09-02T05:40:00Z', votes: 9, voted: true },
  { id: 3, name: 'NUR HANIS BINTI ZAKARIA', mine: false, category: 'bug', module: 'laboratory', body: 'Lab results page logs me out after ten minutes even while I am typing.', status: 'approved', pinned: false, created_at: '2026-09-03T01:05:00Z', votes: 6, voted: false },
]
let nextIdeaId = 100

export function ideas(): { ok: boolean; ideas: Idea[] } {
  return { ok: true, ideas: mockIdeas.map((i) => ({ ...i })) }
}
export function postIdea(body: string, category: IdeaCategory, module: IdeaModule): { ok: boolean; id: number; status: IdeaStatus } {
  const id = nextIdeaId++
  mockIdeas.push({ id, name: mockProfileName, mine: true, category, module, body, status: 'pending', pinned: false, created_at: new Date().toISOString(), votes: 0, voted: false })
  return { ok: true, id, status: 'pending' }
}
export function voteIdea(id: number): { ok: boolean; id: number; voted: boolean; votes: number } {
  const i = mockIdeas.find((x) => x.id === id)
  if (!i) return { ok: false, id, voted: false, votes: 0 }
  i.voted = !i.voted
  i.votes += i.voted ? 1 : -1
  return { ok: true, id, voted: i.voted, votes: i.votes }
}
export function reviewIdea(id: number, patch: { status?: IdeaStatus; pinned?: boolean }): { ok: boolean } {
  const i = mockIdeas.find((x) => x.id === id)
  if (i) Object.assign(i, patch)
  return { ok: Boolean(i) }
}

/* ----------------------------------------------------------- announcements */

import type { Announcement, AnnouncementLevel } from '../lib/types'

const mockAnnouncements: Announcement[] = [
  { id: 1, body: 'Makan tengahari dipindahkan ke 13:15 di Cafe Anjung Sendayan.', level: 'info', active: true, created_at: new Date(Date.now() - 20 * 60_000).toISOString(), expires_at: null },
]
let nextAnnouncementId = 100
export function announcements(): { ok: boolean; announcements: Announcement[] } {
  return { ok: true, announcements: mockAnnouncements.map((a) => ({ ...a })) }
}
export function postAnnouncement(body: string, level: AnnouncementLevel, minutes: number): { ok: boolean; id: number } {
  const id = nextAnnouncementId++
  mockAnnouncements.unshift({ id, body, level, active: true, created_at: new Date().toISOString(), expires_at: minutes > 0 ? new Date(Date.now() + minutes * 60_000).toISOString() : null })
  return { ok: true, id }
}
export function setAnnouncement(id: number, active: boolean): { ok: boolean } {
  const a = mockAnnouncements.find((x) => x.id === id)
  if (a) a.active = active
  return { ok: Boolean(a) }
}
